/**
 * Authenticated admin API for the tenant console. Uses the shared axios client,
 * so every request carries the token and lands on the tenant's own subdomain
 * (X-TENANT is injected by callbox). All of these are org-scoped and require
 * IsOrganizationAdmin / IsOrganizationMember on the server.
 */
import apiClient from './client';

export interface DonationConfig {
  // Writable
  donations_enabled: boolean;
  currency: string;
  min_amount_cents: number;
  statement_descriptor: string;
  // Reference / status (read-only)
  stripe_account_id: string;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
  is_ready: boolean;
}

export type DonationConfigPatch = Partial<
  Pick<
    DonationConfig,
    | 'donations_enabled'
    | 'min_amount_cents'
    | 'statement_descriptor'
  >
>;

export interface StateConfig {
  id: number;
  full_name: string;
  abbreviated_name: string;
  opted_in: boolean;
  sgo_registered: boolean;
  eligible_school_types: string[];
}

// opted_in and eligible_school_types are program-wide — set by SGOKit staff on
// ProgramState and read-only to tenants — so neither is patchable here.
export type StatePatch = Partial<Pick<StateConfig, 'sgo_registered'>>;

// The five SchoolTypeChoices (platform_config.models). Each value is the label
// itself — that's the string the API stores.
export const SCHOOL_TYPES: { value: string; label: string }[] = [
  { value: 'Private', label: 'Private' },
  { value: 'Co-op', label: 'Co-op' },
  { value: 'Online', label: 'Online' },
  { value: 'Hybrid', label: 'Hybrid' },
  { value: 'Homeschool', label: 'Homeschool' },
];

interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export const adminApi = {
  getDonationConfig: (): Promise<DonationConfig> =>
    apiClient.get('/donations/config/').then((r) => r.data),

  updateDonationConfig: (patch: DonationConfigPatch): Promise<DonationConfig> =>
    apiClient.patch('/donations/config/', patch).then((r) => r.data),

  // Creates the Express account if needed and returns a Stripe-hosted onboarding
  // URL to redirect the browser to.
  connectOnboard: (): Promise<{ url: string }> =>
    apiClient.post('/donations/connect/onboard/').then((r) => r.data),

  // A single-use link into the connected account's Express dashboard.
  connectLoginLink: (): Promise<{ url: string }> =>
    apiClient.get('/donations/connect/login-link/').then((r) => r.data),

  listStates: (): Promise<StateConfig[]> =>
    // PAGE_SIZE is 60, so all 51 rows arrive on one page.
    apiClient.get('/states/').then((r) => (r.data as Paginated<StateConfig>).results),

  updateState: (code: string, patch: StatePatch): Promise<StateConfig> =>
    apiClient.patch(`/states/${code}/`, patch).then((r) => r.data),
};
